import type { ServerEvent } from "@catmail/shared";
import { useEffect, useRef } from "react";

/**
 * 서버가 밀어주는 이벤트를 듣는다.
 *
 * EventSource는 끊기면 브라우저가 알아서 다시 붙는다. 다만 세션이 만료돼
 * 401이 떨어지는 경우에는 무한 재접속이 되므로, 그때는 우리가 닫는다.
 */
export function useServerEvents(
  enabled: boolean,
  onEvent: (event: ServerEvent) => void,
): void {
  // 콜백이 매 렌더 바뀌어도 연결을 다시 맺지 않도록 ref에 담아 둔다.
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;

    const source = new EventSource("/api/events", { withCredentials: true });

    source.onmessage = (message) => {
      try {
        handlerRef.current(JSON.parse(message.data) as ServerEvent);
      } catch {
        // 조각난 이벤트는 무시한다. 다음 것이 온다.
      }
    };

    source.onerror = () => {
      // CLOSED면 브라우저가 재시도를 포기한 것이다(대개 인증 실패).
      if (source.readyState === EventSource.CLOSED) source.close();
    };

    return () => source.close();
  }, [enabled]);
}
